import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DollarSign, ShoppingBag, Eye, Mail, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

const COLORS = ['#8B5CF6', '#00FFFF', '#00CC99', '#0075FF', '#EC4899'];

/**
 * Iter 183 — Hoisted `CustomTooltip` to module scope.
 * Previously this was defined inside `DashboardAnalytics`, meaning React
 * saw a NEW component type on every parent re-render and destroyed the
 * entire tooltip subtree. This is the same "unstable nested component"
 * anti-pattern that caused the website-builder canvas hero flicker
 * (see HeroBlock.js iter 183 fix). Not a user-facing bug here because
 * the tooltip only appears on chart hover, but flagged by the lint
 * audit and worth cleaning up for consistency.
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#080A19] border border-white/10 rounded-lg shadow-xl px-3 py-2 text-xs">
      <p className="text-white font-medium mb-1">{label}</p>
      {payload.map((p, i) => <p key={i} style={{ color: p.color }}>{p.name}: {typeof p.value === 'number' ? `$${p.value.toFixed(2)}` : p.value}</p>)}
    </div>
  );
};

export default function DashboardAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/analytics`, { headers: { Authorization: `Bearer ${token}` } });
        setAnalytics(response.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-[#8B5CF6]" /></div>;
  }

  const stats = [
    { label: 'Total Sales', value: `$${(analytics?.total_sales || 0).toFixed(2)}`, change: analytics?.recent_sales > 0 ? `+$${analytics.recent_sales.toFixed(2)} this month` : 'No sales yet', icon: DollarSign, gradient: 'from-[#00CC99] to-[#10B981]', trend: analytics?.recent_sales > 0 ? 'up' : 'neutral' },
    { label: 'Total Orders', value: analytics?.total_orders || 0, change: `${analytics?.recent_orders || 0} this month`, icon: ShoppingBag, gradient: 'from-[#3B82F6] to-[#06B6D4]', trend: analytics?.recent_orders > 0 ? 'up' : 'neutral' },
    { label: 'Page Views', value: analytics?.total_views || 0, change: 'All time', icon: Eye, gradient: 'from-[#8B5CF6] to-[#EC4899]', trend: 'neutral' },
    { label: 'Conversion', value: `${analytics?.conversion_rate || 0}%`, change: 'Orders / Views', icon: TrendingUp, gradient: 'from-[#F59E0B] to-[#EF4444]', trend: analytics?.conversion_rate > 2 ? 'up' : 'neutral' },
  ];

  const topProductsData = analytics?.top_products?.map((product, index) => ({ name: product._id || `Product ${index + 1}`, value: product.count || 0, revenue: product.revenue || 0 })) || [];

  return (
    <div className="space-y-8" data-testid="dashboard-analytics">
      <div>
        <h1 className="text-2xl font-bold text-white" data-testid="analytics-title">Analytics</h1>
        <p className="text-[#9CA3AF] text-sm mt-1">Track your store's performance</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="analytics-stats">
        {stats.map((stat) => (
          <GlassCard key={stat.label} className="p-5 hover:shadow-[0_0_25px_rgba(0,255,255,0.1)] hover:border-cyan-500/20 transition-all duration-300" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-[#64748B] uppercase tracking-wider font-medium">{stat.label}</p>
                <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                <div className="flex items-center gap-1 mt-1">
                  {stat.trend === 'up' && <TrendingUp size={12} className="text-[#00CC99]" />}
                  {stat.trend === 'down' && <TrendingDown size={12} className="text-[#FF6B6B]" />}
                  <span className="text-[10px] text-[#64748B]">{stat.change}</span>
                </div>
              </div>
              <div className={`p-2.5 rounded-xl bg-gradient-to-br ${stat.gradient} shadow-lg`}>
                <stat.icon size={16} className="text-white" />
              </div>
            </div>
          </GlassCard>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <GlassCard className="p-6" data-testid="sales-chart">
          <h2 className="font-semibold text-lg text-white mb-6">Sales Trend</h2>
          {analytics?.daily_sales?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={analytics.daily_sales}>
                <defs>
                  <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00FFFF" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#00FFFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="sales" stroke="#00FFFF" fill="url(#salesGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-[#64748B]">No sales data yet</div>
          )}
        </GlassCard>

        <GlassCard className="p-6" data-testid="top-products-chart">
          <h2 className="font-semibold text-lg text-white mb-6">Top Products</h2>
          {topProductsData.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie data={topProductsData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={false}>
                    {topProductsData.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Units']} contentStyle={{ backgroundColor: '#080A19', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {topProductsData.map((product, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} /><span className="text-[#94A3B8] truncate max-w-[120px]">{product.name}</span></div>
                    <span className="text-[#64748B]">{product.value} sold</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-[#64748B]">No product data yet</div>
          )}
        </GlassCard>
      </div>

      {/* Email & Performance */}
      <div className="grid lg:grid-cols-2 gap-6">
        <GlassCard className="p-6" data-testid="signups-stats">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg text-white">Subscribers</h2>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20">
              <Mail size={14} className="text-[#00CC99]" />
              <span className="text-[#00CC99] text-sm font-medium">{analytics?.total_signups || 0}</span>
            </div>
          </div>
          <p className="text-[#9CA3AF] text-sm">Total email subscribers. Export from the Messages tab.</p>
        </GlassCard>

        <GlassCard className="p-6" data-testid="performance-summary">
          <h2 className="font-semibold text-lg text-white mb-4">Performance</h2>
          <div className="space-y-3 text-sm">
            {[
              { label: 'Average Order', value: `$${analytics?.total_orders > 0 ? (analytics.total_sales / analytics.total_orders).toFixed(2) : '0.00'}` },
              { label: 'Orders This Month', value: analytics?.recent_orders || 0 },
              { label: 'Revenue This Month', value: `$${(analytics?.recent_sales || 0).toFixed(2)}` },
              { label: 'New Subscribers', value: analytics?.total_signups || 0 },
            ].map((item) => (
              <div key={item.label} className="flex justify-between py-2 border-b border-white/5 last:border-0">
                <span className="text-[#9CA3AF]">{item.label}</span>
                <span className="text-white font-medium">{item.value}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
